import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import Select from "react-select";
import Loader from "../Common/Loader";
import { useDispatch } from "react-redux";
import {
  createCampaignAction,
  updateCampaignAction,
} from "../../../store/actions/CampaignAction";

const AddCampaign = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { item } = location.state || {};
  const isEditMode = !!item;

  const today = new Date().toISOString().split("T")[0];

  const statusOptions = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
  ];

  const [form, setForm] = useState({
    title: "",
    description: "",
    start_date: "",
    end_date: "",
    consultation_charges: "",
    medicinal_discount: "",
    campaign_status: "", // No default value - user must select
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  /* ---------------- EDIT MODE LOAD ---------------- */
  useEffect(() => {
    if (isEditMode && item) {
      setForm({
        title: item.title || "",
        description: item.description || "",
        start_date: item.start_date || "",
        end_date: item.end_date || "",
        consultation_charges: item.consultation_charges || "",
        medicinal_discount: item.medicinal_discount || "",
        campaign_status: item.campaign_status === "Active" || item.campaign_status === 1 ? 1 : 
                         item.campaign_status === "Inactive" || item.campaign_status === 0 ? 0 : "",
      });
    }
  }, [isEditMode, item]);

  /* ---------------- VALIDATION ---------------- */
  const validateForm = () => {
    const newErrors = {};

    if (!form.title?.trim()) {
      newErrors.title = "Title is required";
    }

    if (!form.start_date) {
      newErrors.start_date = "Start date is required";
    } else if (
      !isEditMode &&
      new Date(form.start_date) < new Date(today)
    ) {
      newErrors.start_date = "Start date cannot be in the past";
    }

    if (!form.end_date) {
      newErrors.end_date = "End date is required";
    } else if (
      new Date(form.end_date) < new Date(form.start_date)
    ) {
      newErrors.end_date = "End date must be after start date";
    }

    if (!form.consultation_charges) {
      newErrors.consultation_charges = "Consultation charges are required";
    } else if (
      isNaN(form.consultation_charges) ||
      Number(form.consultation_charges) <= 0
    ) {
      newErrors.consultation_charges =
        "Consultation charges must be a valid number";
    }

    // ✅ Status Validation Added
    if (!form.campaign_status && form.campaign_status !== 0) {
      newErrors.campaign_status = "Status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ---------------- FIELD CHANGE ---------------- */
  const handleFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "start_date" && { end_date: "" }),
    }));

    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  };

  /* ---------------- STATUS CHANGE ---------------- */
  const handleStatusChange = (selectedOption) => {
    setForm({
      ...form,
      campaign_status: selectedOption?.value || "",
    });
    // Clear status error when user selects
    if (errors.campaign_status) {
      const { campaign_status, ...rest } = errors;
      setErrors(rest);
    }
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    const payload = {
      title: form.title,
      description: form.description,
      start_date: form.start_date,
      end_date: form.end_date,
      consultation_charges: form.consultation_charges,
      medicinal_discount: form.medicinal_discount,
      campaign_status: form.campaign_status === 1 ? "Active" : "Inactive",
    };

    try {
      if (isEditMode) {
        payload.campaign_id = item.campaign_id;
        await dispatch(
          updateCampaignAction(payload, item.campaign_id, navigate)
        );
      } else {
        await dispatch(createCampaignAction(payload, navigate));
      }
    } catch (error) {
      console.error("Campaign save failed", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row">
      <div className="col-xl-12">

        <div className="d-flex justify-content-end mb-3">
          <Link to="/campaign">
            <Button variant="primary">Back</Button>
          </Link>
        </div>

        <div className="card">
          <div className="card-body">

            <form onSubmit={handleSubmit}>
              <div className="row">

                {/* Title */}
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.title}
                    onChange={(e) =>
                      handleFieldChange("title", e.target.value)
                    }
                  />
                  {errors.title && (
                    <div className="text-danger">{errors.title}</div>
                  )}
                </div>

                {/* Consultation Charges */}
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Consultation Charges (₹)
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.consultation_charges}
                    onChange={(e) =>
                      handleFieldChange("consultation_charges", e.target.value)
                    }
                  />
                  {errors.consultation_charges && (
                    <div className="text-danger">
                      {errors.consultation_charges}
                    </div>
                  )}
                </div>

                {/* Medicinal Discount */}
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Medicinal Discount (%)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.medicinal_discount}
                    onChange={(e) =>
                      handleFieldChange("medicinal_discount", e.target.value)
                    }
                  />
                </div>

                {/* Start Date */}
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.start_date || ""}
                    min={isEditMode ? undefined : today}
                    onChange={(e) =>
                      handleFieldChange("start_date", e.target.value)
                    }
                  />
                  {errors.start_date && (
                    <div className="text-danger">{errors.start_date}</div>
                  )}
                </div>

                {/* End Date */}
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.end_date || ""}
                    min={form.start_date ? form.start_date : today}
                    onChange={(e) =>
                      handleFieldChange("end_date", e.target.value)
                    }
                  />
                  {errors.end_date && (
                    <div className="text-danger">{errors.end_date}</div>
                  )}
                </div>

                {/* Description */}
                <div className="col-xl-12 mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    rows="3"
                    className="form-control"
                    value={form.description}
                    onChange={(e) =>
                      handleFieldChange("description", e.target.value)
                    }
                  />
                </div>

                {/* Status - With Validation */}
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Status <span className="text-danger">*</span>
                  </label>

                  <Select
                    options={statusOptions}
                    value={statusOptions.find(
                      (opt) => opt.value === form.campaign_status
                    )}
                    onChange={handleStatusChange}
                    placeholder="Select Status"
                    isClearable
                  />
                  {errors.campaign_status && (
                    <div className="text-danger">{errors.campaign_status}</div>
                  )}
                </div>

              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader size="sm" />
                    <span className="ms-2">
                      {isEditMode ? "Updating..." : "Submitting..."}
                    </span>
                  </>
                ) : (
                  <>{isEditMode ? "Update Campaign" : "Add Campaign"}</>
                )}
              </button>

            </form>

          </div>
        </div>

      </div>
    </div>
  );
};

export default AddCampaign;